import React, { useState, useEffect, useCallback } from 'react';
import { db, storage } from '../../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { UploadCloud } from 'lucide-react';

const ManageSiteContent = () => {
    const [content, setContent] = useState({
        // SEO
        seoTitle: '', seoDescription: '',
        // SEO Local
        streetAddress: '', addressLocality: '', postalCode: '', telephone: '',
        // Logo e Nome
        siteName: 'GOLDEN BLADE', logoUrl: '', logoSize: 40, logoAlt: '',
        // Hero
        heroTitle: '', heroSubtitle: '', heroImageUrl: '', heroImageAlt: '', schedulingLink: '', heroImagePosition: 'center',
        // About
        aboutTitle: '', aboutSubtitle: '', aboutImageUrl: '', aboutImageAlt: '',
        // Services
        servicesTitle: '', servicesSubtitle: '', servicesImageUrl: '', servicesImageAlt: '',
        // Gallery
        galleryTitle: '',
        // Testimonials
        testimonialsTitle: '',
        // Contact
        contactTitle: '', contactSubtitle: '', addressText: '', mapsLink: '', phoneText: '', whatsappLink: '',
        // Footer
        footerText: '',
        // Aparência
        showGallery: true, primaryColor: '#d97706', buttonColor: '#d97706', buttonTextColor: '#000000', backgroundColor: '#171717', textColor: '#e5e5e5',
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);
    const [isUploadingHero, setIsUploadingHero] = useState(false);
    const [isUploadingServices, setIsUploadingServices] = useState(false);
    const [isUploadingAbout, setIsUploadingAbout] = useState(false);

    const contentDocRef = useCallback(() => doc(db, "siteContent", "main"), []);

    useEffect(() => {
        const fetchContent = async () => {
            const docSnap = await getDoc(contentDocRef());
            if (docSnap.exists()) {
                setContent(prev => ({ ...prev, ...docSnap.data() }));
            }
            setIsLoading(false);
        };
        fetchContent();
    }, [contentDocRef]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setContent(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleImageUpload = async (event, section) => {
        const file = event.target.files[0];
        if (!file) return;

        let uploaderStateSetter;
        if (section === 'logo') uploaderStateSetter = setIsUploadingLogo;
        else if (section === 'hero') uploaderStateSetter = setIsUploadingHero;
        else if (section === 'services') uploaderStateSetter = setIsUploadingServices;
        else if (section === 'about') uploaderStateSetter = setIsUploadingAbout;
        
        uploaderStateSetter(true);

        const imageRef = ref(storage, `${section}/${file.name + uuidv4()}`);
        try {
            const snapshot = await uploadBytes(imageRef, file);
            const url = await getDownloadURL(snapshot.ref);
            
            let imageUrlField;
            if (section === 'logo') imageUrlField = 'logoUrl';
            else if (section === 'hero') imageUrlField = 'heroImageUrl';
            else if (section === 'services') imageUrlField = 'servicesImageUrl';
            else if (section === 'about') imageUrlField = 'aboutImageUrl';

            setContent(prev => ({ ...prev, [imageUrlField]: url }));

        } catch (error) {
            console.error("Erro no upload:", error);
            alert("Falha ao enviar imagem.");
        } finally {
            uploaderStateSetter(false);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await setDoc(contentDocRef(), content, { merge: true });
            alert('Configurações salvas com sucesso!');
        } catch (error) {
            alert('Falha ao salvar as configurações.');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) return <p>A carregar...</p>;

    return (
        <div>
            <h1 className="text-2xl font-bold text-white mb-6">Conteúdo Geral e Aparência</h1>
            
            <div className="bg-neutral-800 p-6 rounded-lg mb-8">
                <h2 className="text-xl font-bold text-white mb-4">SEO (Otimização para Google)</h2>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Título do Site (para o Google)</label>
                        <input name="seoTitle" value={content.seoTitle} onChange={handleChange} placeholder="Ex: Hulk Cabeleireiro - A Melhor Barbearia de..." className="w-full p-2 bg-neutral-700 rounded border border-neutral-600" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Descrição do Site (para o Google)</label>
                        <textarea name="seoDescription" value={content.seoDescription} onChange={handleChange} placeholder="Descreva a sua barbearia em poucas palavras." className="w-full p-2 bg-neutral-700 rounded border border-neutral-600" rows="3" />
                    </div>
                </div>
                <div className="mt-6 p-4 border border-neutral-700 rounded-lg space-y-4">
                    <h3 className="font-semibold text-lg text-gray-300">Informações do Negócio (SEO Local)</h3>
                    <p className="text-xs text-gray-400">Preencha estes campos para melhorar a sua visibilidade no Google Maps e pesquisas locais.</p>
                    <input name="streetAddress" value={content.streetAddress} onChange={handleChange} placeholder="Endereço (Rua, Número)" className="w-full p-2 bg-neutral-700 rounded" />
                    <input name="addressLocality" value={content.addressLocality} onChange={handleChange} placeholder="Cidade" className="w-full p-2 bg-neutral-700 rounded" />
                    <input name="postalCode" value={content.postalCode} onChange={handleChange} placeholder="Código Postal" className="w-full p-2 bg-neutral-700 rounded" />
                    <input name="telephone" value={content.telephone} onChange={handleChange} placeholder="Telefone (formato internacional, ex: +351...)" className="w-full p-2 bg-neutral-700 rounded" />
                </div>
            </div>

            <div className="bg-neutral-800 p-6 rounded-lg mb-8">
                <h2 className="text-xl font-bold text-white mb-4">Identidade Visual</h2>
                 <div className="grid md:grid-cols-2 gap-6 items-start">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Nome do Site</label>
                        <input name="siteName" value={content.siteName} onChange={handleChange} placeholder="Nome da Barbearia" className="w-full p-2 bg-neutral-700 rounded border border-neutral-600" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Logo</label>
                        <div className="mt-2">
                            {content.logoUrl ? (
                                <div className="relative group w-48 rounded-lg overflow-hidden">
                                    <img src={content.logoUrl} alt="Logo atual" className="w-full h-auto object-contain bg-neutral-900 p-2" />
                                    {isUploadingLogo && <div className="absolute inset-0 bg-black/70 flex items-center justify-center"><p className="text-white">A enviar...</p></div>}
                                    <label htmlFor="logo-image-upload" className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-semibold opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">Trocar Logo</label>
                                </div>
                            ) : (
                                <label htmlFor="logo-image-upload" className="w-48 h-24 flex flex-col items-center justify-center border-2 border-dashed border-neutral-600 rounded-lg cursor-pointer hover:bg-neutral-700">
                                    <UploadCloud size={30} className="text-gray-400 mb-2" />
                                    <span className="text-gray-400 text-xs">Enviar Logo</span>
                                </label>
                            )}
                            <input id="logo-image-upload" type="file" className="hidden" onChange={(e) => handleImageUpload(e, 'logo')} disabled={isUploadingLogo} />
                        </div>
                        <label className="block text-sm font-medium text-gray-300 mt-2 mb-1">Descrição do Logo (SEO)</label>
                        <input name="logoAlt" value={content.logoAlt} onChange={handleChange} placeholder="Ex: Logo da Barbearia Hulk" className="w-full p-2 bg-neutral-700 rounded border border-neutral-600" />
                    </div>
                </div>
                <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-300 mb-1">Tamanho do Logo (Altura): {content.logoSize}px</label>
                    <input 
                        type="range" 
                        name="logoSize" 
                        min="20" 
                        max="100" 
                        value={content.logoSize} 
                        onChange={handleChange} 
                        className="w-full h-2 bg-neutral-700 rounded-lg appearance-none cursor-pointer"
                    />
                </div>
            </div>

            <div className="bg-neutral-800 p-6 rounded-lg mb-8">
                <h2 className="text-xl font-bold text-white mb-4">Aparência do Site</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Cor Principal</label>
                        <input type="color" name="primaryColor" value={content.primaryColor} onChange={handleChange} className="w-full h-10 p-1 bg-neutral-700 rounded border border-neutral-600 cursor-pointer" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Cor do Botão</label>
                        <input type="color" name="buttonColor" value={content.buttonColor} onChange={handleChange} className="w-full h-10 p-1 bg-neutral-700 rounded border border-neutral-600 cursor-pointer" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Texto do Botão</label>
                        <input type="color" name="buttonTextColor" value={content.buttonTextColor} onChange={handleChange} className="w-full h-10 p-1 bg-neutral-700 rounded border border-neutral-600 cursor-pointer" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Cor de Fundo</label>
                        <input type="color" name="backgroundColor" value={content.backgroundColor} onChange={handleChange} className="w-full h-10 p-1 bg-neutral-700 rounded border border-neutral-600 cursor-pointer" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Cor do Texto</label>
                        <input type="color" name="textColor" value={content.textColor} onChange={handleChange} className="w-full h-10 p-1 bg-neutral-700 rounded border border-neutral-600 cursor-pointer" />
                    </div>
                </div>
                 <div className="flex items-center">
                    <input type="checkbox" id="showGallery" name="showGallery" checked={content.showGallery} onChange={handleChange} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
                    <label htmlFor="showGallery" className="ml-2 block text-sm text-gray-300">Exibir secção "A Nossa Galeria"?</label>
                </div>
            </div>

            <div className="bg-neutral-800 p-6 rounded-lg space-y-6">
                <h2 className="text-xl font-bold text-white mb-4">Conteúdo da Página</h2>
                
                <div className="p-4 border border-neutral-700 rounded-lg space-y-4">
                    <h3 className="font-semibold text-lg text-gray-300">Secção Principal (Hero)</h3>
                    <input name="heroTitle" value={content.heroTitle} onChange={handleChange} placeholder="Título" className="w-full p-2 bg-neutral-700 rounded" />
                    <input name="heroSubtitle" value={content.heroSubtitle} onChange={handleChange} placeholder="Subtítulo" className="w-full p-2 bg-neutral-700 rounded" />
                    <label className="block text-sm font-medium text-gray-300">Imagem de Fundo</label>
                     <div className="mt-2">
                        {content.heroImageUrl ? (
                            <div className="relative group w-full max-w-md rounded-lg overflow-hidden">
                                <img src={content.heroImageUrl} alt="Imagem de fundo atual" className="w-full h-auto object-cover" />
                                {isUploadingHero && <div className="absolute inset-0 bg-black/70 flex items-center justify-center"><p className="text-white">A enviar...</p></div>}
                                <label htmlFor="hero-image-upload" className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-semibold opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">Trocar Imagem</label>
                            </div>
                        ) : (
                            <label htmlFor="hero-image-upload" className="w-full max-w-md h-48 flex flex-col items-center justify-center border-2 border-dashed border-neutral-600 rounded-lg cursor-pointer hover:bg-neutral-700">
                                <UploadCloud size={40} className="text-gray-400 mb-2" />
                                <span className="text-gray-400">Clique para enviar uma imagem</span>
                            </label>
                        )}
                        <input id="hero-image-upload" type="file" className="hidden" onChange={(e) => handleImageUpload(e, 'hero')} disabled={isUploadingHero} />
                    </div>
                    <label className="block text-sm font-medium text-gray-300 mt-2 mb-1">Descrição da Imagem de Fundo (SEO)</label>
                    <input name="heroImageAlt" value={content.heroImageAlt} onChange={handleChange} placeholder="Ex: Cliente a receber um corte de cabelo" className="w-full p-2 bg-neutral-700 rounded border border-neutral-600" />
                    <label className="block text-sm font-medium text-gray-300">Foco da Imagem</label>
                    <select name="heroImagePosition" value={content.heroImagePosition} onChange={handleChange} className="w-full p-2 bg-neutral-700 rounded border border-neutral-600">
                        <option value="top">Topo</option>
                        <option value="center">Centro</option>
                        <option value="bottom">Base</option>
                    </select>
                </div>

                <div className="p-4 border border-neutral-700 rounded-lg space-y-4">
                    <h3 className="font-semibold text-lg text-gray-300">Secção Sobre</h3>
                    <input name="aboutTitle" value={content.aboutTitle} onChange={handleChange} placeholder="Título" className="w-full p-2 bg-neutral-700 rounded" />
                    <textarea name="aboutSubtitle" value={content.aboutSubtitle} onChange={handleChange} placeholder="Texto da secção" className="w-full p-2 bg-neutral-700 rounded" rows="4" />
                    <label className="block text-sm font-medium text-gray-300">Imagem da Secção</label>
                    <div className="mt-2">
                        {content.aboutImageUrl ? (
                            <div className="relative group w-full max-w-md rounded-lg overflow-hidden">
                                <img src={content.aboutImageUrl} alt="Imagem da secção sobre" className="w-full h-auto object-cover" />
                                {isUploadingAbout && <div className="absolute inset-0 bg-black/70 flex items-center justify-center"><p className="text-white">A enviar...</p></div>}
                                <label htmlFor="about-image-upload" className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-semibold opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">Trocar Imagem</label>
                            </div>
                        ) : (
                            <label htmlFor="about-image-upload" className="w-full max-w-md h-48 flex flex-col items-center justify-center border-2 border-dashed border-neutral-600 rounded-lg cursor-pointer hover:bg-neutral-700">
                                <UploadCloud size={40} className="text-gray-400 mb-2" />
                                <span className="text-gray-400">Clique para enviar uma imagem</span>
                            </label>
                        )}
                        <input id="about-image-upload" type="file" className="hidden" onChange={(e) => handleImageUpload(e, 'about')} disabled={isUploadingAbout} />
                    </div>
                    <label className="block text-sm font-medium text-gray-300 mt-2 mb-1">Descrição da Imagem Sobre (SEO)</label>
                    <input name="aboutImageAlt" value={content.aboutImageAlt} onChange={handleChange} placeholder="Ex: Interior da barbearia com cadeiras de couro" className="w-full p-2 bg-neutral-700 rounded border border-neutral-600" />
                </div>

                <div className="p-4 border border-neutral-700 rounded-lg space-y-4">
                    <h3 className="font-semibold text-lg text-gray-300">Secção de Serviços</h3>
                    <input name="servicesTitle" value={content.servicesTitle} onChange={handleChange} placeholder="Título" className="w-full p-2 bg-neutral-700 rounded" />
                    <input name="servicesSubtitle" value={content.servicesSubtitle} onChange={handleChange} placeholder="Subtítulo" className="w-full p-2 bg-neutral-700 rounded" />
                    <label className="block text-sm font-medium text-gray-300">Imagem de Fundo (Opcional)</label>
                    <div className="mt-2">
                        {content.servicesImageUrl ? (
                            <div className="relative group w-full max-w-md rounded-lg overflow-hidden">
                                <img src={content.servicesImageUrl} alt="Imagem de fundo dos serviços" className="w-full h-auto object-cover" />
                                {isUploadingServices && <div className="absolute inset-0 bg-black/70 flex items-center justify-center"><p className="text-white">A enviar...</p></div>}
                                <label htmlFor="services-image-upload" className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-semibold opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">Trocar Imagem</label>
                            </div>
                        ) : (
                            <label htmlFor="services-image-upload" className="w-full max-w-md h-48 flex flex-col items-center justify-center border-2 border-dashed border-neutral-600 rounded-lg cursor-pointer hover:bg-neutral-700">
                                <UploadCloud size={40} className="text-gray-400 mb-2" />
                                <span className="text-gray-400">Clique para enviar uma imagem</span>
                            </label>
                        )}
                        <input id="services-image-upload" type="file" className="hidden" onChange={(e) => handleImageUpload(e, 'services')} disabled={isUploadingServices} />
                    </div>
                    <label className="block text-sm font-medium text-gray-300 mt-2 mb-1">Descrição da Imagem de Serviços (SEO)</label>
                    <input name="servicesImageAlt" value={content.servicesImageAlt} onChange={handleChange} placeholder="Ex: Barbeiro a usar uma navalha" className="w-full p-2 bg-neutral-700 rounded border border-neutral-600" />
                </div>

                <div className="p-4 border border-neutral-700 rounded-lg space-y-4">
                    <h3 className="font-semibold text-lg text-gray-300">Secção da Galeria</h3>
                    <input name="galleryTitle" value={content.galleryTitle} onChange={handleChange} placeholder="Título" className="w-full p-2 bg-neutral-700 rounded" />
                </div>

                <div className="p-4 border border-neutral-700 rounded-lg space-y-4">
                    <h3 className="font-semibold text-lg text-gray-300">Secção de Depoimentos</h3>
                    <input name="testimonialsTitle" value={content.testimonialsTitle} onChange={handleChange} placeholder="Título" className="w-full p-2 bg-neutral-700 rounded" />
                </div>

                <div className="p-4 border border-neutral-700 rounded-lg space-y-4">
                    <h3 className="font-semibold text-lg text-gray-300">Secção de Contacto</h3>
                    <input name="contactTitle" value={content.contactTitle} onChange={handleChange} placeholder="Título" className="w-full p-2 bg-neutral-700 rounded" />
                    <input name="contactSubtitle" value={content.contactSubtitle} onChange={handleChange} placeholder="Subtítulo" className="w-full p-2 bg-neutral-700 rounded" />
                    <input name="addressText" value={content.addressText} onChange={handleChange} placeholder="Texto do Endereço (ex: Av. Principal, 123)" className="w-full p-2 bg-neutral-700 rounded" />
                    <input name="mapsLink" value={content.mapsLink} onChange={handleChange} placeholder="Link do Google Maps para o endereço" className="w-full p-2 bg-neutral-700 rounded" />
                    <input name="phoneText" value={content.phoneText} onChange={handleChange} placeholder="Texto do Telefone (ex: (11) 9.8765-4321)" className="w-full p-2 bg-neutral-700 rounded" />
                    <input name="whatsappLink" value={content.whatsappLink} onChange={handleChange} placeholder="Link do WhatsApp (wa.me/...)" className="w-full p-2 bg-neutral-700 rounded" />
                </div>

                 <div className="p-4 border border-neutral-700 rounded-lg space-y-4">
                    <h3 className="font-semibold text-lg text-gray-300">Link de Agendamento</h3>
                    <input name="schedulingLink" value={content.schedulingLink} onChange={handleChange} placeholder="https://seu-site.com/agendar" className="w-full p-2 bg-neutral-700 rounded" />
                </div>
                
                <div className="p-4 border border-neutral-700 rounded-lg space-y-4">
                    <h3 className="font-semibold text-lg text-gray-300">Rodapé</h3>
                    <input name="footerText" value={content.footerText} onChange={handleChange} placeholder="Texto do rodapé (ex: © 2025 Golden Blade)" className="w-full p-2 bg-neutral-700 rounded" />
                </div>
            </div>
            
            <div className="mt-6">
                <button onClick={handleSave} disabled={isSaving || isUploadingLogo || isUploadingHero || isUploadingServices || isUploadingAbout} className="bg-button text-button-text font-bold px-6 py-2 rounded-lg hover:opacity-90 disabled:bg-gray-500">
                    {isSaving ? 'A guardar...' : 'Guardar Configurações'}
                </button>
            </div>
        </div>
    );
};

export default ManageSiteContent;
